import type { AxiosError } from 'axios';

export const ApiErrorCode = Object.freeze({
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CONNECTION_REFUSED: 'CONNECTION_REFUSED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  BAD_REQUEST: 'BAD_REQUEST',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  CANCELLED: 'CANCELLED',
  UNKNOWN: 'UNKNOWN',
} as const);

export type ApiErrorCodeValue = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

type BackendErrorData = Record<string, unknown> & {
  ErrorDetails?: string;
  errorDetails?: string;
  Message?: string;
  message?: string;
  error?: string;
};

function normalizeBackendMessage(message: string | null | undefined): string | null {
  if (!message || typeof message !== 'string') return null;

  const lower = message.toLowerCase().trim();

  const messageMap: Array<[RegExp, string | null]> = [
    [/token.*expired/i, 'Your session has expired. Please login again.'],
    [/invalid token/i, 'Your session is invalid. Please login again.'],
    [/unauthorized/i, 'You are not authorized to perform this action.'],
    [/access denied/i, 'You do not have permission to access this resource.'],
    [/required/i, 'Please fill in all required fields.'],
    [/cannot be null/i, 'A required value is missing.'],
    [/invalid.*format/i, 'The format of some input is invalid.'],
    [/too long/i, 'One or more values are too long.'],
    [/too short/i, 'One or more values are too short.'],
    [/already exists/i, 'This record already exists.'],
    [/duplicate/i, 'A duplicate record was found.'],
    [/in use.*cannot delete/i, 'This record cannot be deleted because it is in use.'],
    [/something went wrong/i, null],
    [/an error occurred/i, null],
    [/operation failed/i, null],
    [/request failed/i, null],
    [/internal server error/i, null],
  ];

  for (const [pattern, friendly] of messageMap) {
    if (pattern.test(lower)) {
      return friendly;
    }
  }

  return null;
}

export class ApiError extends Error {
  status: number;
  code: ApiErrorCodeValue;
  data: BackendErrorData | null;
  timestamp: string;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCodeValue,
    data: BackendErrorData | null = null
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.timestamp = new Date().toISOString();
  }

  static fromAxiosError(error: AxiosError<BackendErrorData>): ApiError {
    if (error.code === 'ECONNABORTED') {
      return new ApiError(
        'Request timed out. Please try again.',
        408,
        ApiErrorCode.TIMEOUT
      );
    }

    if (!error.response) {
      return new ApiError(
        'Network error. Please check your connection.',
        0,
        ApiErrorCode.NETWORK_ERROR
      );
    }

    const { status, data } = error.response;
    const backendMessage =
      data?.ErrorDetails ||
      data?.errorDetails ||
      data?.Message ||
      data?.message ||
      data?.error ||
      error.message ||
      'An error occurred';

    switch (status) {
      case 400:
        return new ApiError(backendMessage, status, ApiErrorCode.BAD_REQUEST, data);
      case 401:
        return new ApiError(
          backendMessage === 'An error occurred'
            ? 'Session expired. Please login again.'
            : backendMessage,
          status,
          ApiErrorCode.UNAUTHORIZED,
          data
        );
      case 403:
        return new ApiError(
          'Access denied. You do not have permission.',
          status,
          ApiErrorCode.FORBIDDEN,
          data
        );
      case 404:
        return new ApiError(
          backendMessage === 'An error occurred' ? 'Resource not found.' : backendMessage,
          status,
          ApiErrorCode.NOT_FOUND,
          data
        );
      case 409:
        return new ApiError(backendMessage, status, ApiErrorCode.CONFLICT, data);
      case 422:
        return new ApiError(backendMessage, status, ApiErrorCode.VALIDATION_ERROR, data);
      case 500:
        return new ApiError(
          backendMessage === 'An error occurred'
            ? 'Server error. Please try again later.'
            : backendMessage,
          status,
          ApiErrorCode.INTERNAL_ERROR,
          data
        );
      case 503:
        // Spring's own 503s say why and what to do (e.g. "The server is busy preparing
        // other reports. Please try again in a moment."); a proxy's 503 has no JSON message.
        if (typeof (data?.Message || data?.message) === 'string') {
          return new ApiError(
            String(data?.Message || data?.message),
            status,
            ApiErrorCode.SERVICE_UNAVAILABLE,
            data
          );
        }
        return new ApiError(
          'Service temporarily unavailable.',
          status,
          ApiErrorCode.SERVICE_UNAVAILABLE,
          data
        );
      case 502:
      case 504:
        return new ApiError(
          'Service temporarily unavailable.',
          status,
          ApiErrorCode.SERVICE_UNAVAILABLE,
          data
        );
      default:
        return new ApiError(backendMessage, status, ApiErrorCode.UNKNOWN, data);
    }
  }

  static fromCancel(): ApiError {
    return new ApiError('Request cancelled', 0, ApiErrorCode.CANCELLED);
  }

  isUnauthorized(): boolean {
    return this.code === ApiErrorCode.UNAUTHORIZED || this.code === ApiErrorCode.TOKEN_EXPIRED;
  }

  isForbidden(): boolean {
    return this.code === ApiErrorCode.FORBIDDEN;
  }

  isNetworkError(): boolean {
    return this.code === ApiErrorCode.NETWORK_ERROR || this.code === ApiErrorCode.TIMEOUT;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }

  getUserMessage(): string {
    const normalized = normalizeBackendMessage(this.message);
    if (normalized !== null) {
      return normalized;
    }

    if (
      this.message &&
      !['An error occurred', 'Request timed out. Please try again.'].includes(this.message)
    ) {
      return this.message;
    }

    switch (this.code) {
      case ApiErrorCode.NETWORK_ERROR:
        return 'Network error. Please check your connection.';
      case ApiErrorCode.TIMEOUT:
        return 'Request timed out. Please try again.';
      case ApiErrorCode.UNAUTHORIZED:
        return 'Session expired. Please login again.';
      case ApiErrorCode.FORBIDDEN:
        return 'Access denied. You do not have permission.';
      case ApiErrorCode.NOT_FOUND:
        return 'Resource not found.';
      case ApiErrorCode.VALIDATION_ERROR:
        return 'Validation error. Please check your input.';
      case ApiErrorCode.SERVICE_UNAVAILABLE:
        return 'Service temporarily unavailable. Please try again later.';
      case ApiErrorCode.INTERNAL_ERROR:
        return 'Server error. Please try again later.';
      case ApiErrorCode.CANCELLED:
        return 'Request cancelled.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      code: this.code,
      data: this.data,
      timestamp: this.timestamp,
    };
  }
}

export default ApiError;

